

import { Action, Getter } from "vuex-class"
import { Component, Vue, Watch } from "vue-property-decorator"
import moment from "moment"

import { Subscription } from "@/types/subscription"

import { BvTableField } from "bootstrap-vue"
import { CurrentUser } from "@/types/user"

@Component({
	components: {
		"page": () => import("@/components/New/Page/PageWrapper.vue"),
		"skeleton-loader": () => import("@/components/SkeletonLoader.vue")
	},
	filters: {
		capitalize: function (value: string) {
			if (!value) return ""

			return value.charAt(0).toUpperCase() + value.slice(1)
		}
	}
})
export default class SubscriptionCard extends Vue {
	@Getter("getMe") currentUser!: CurrentUser;
	@Getter("getSubscription") subscription?: Subscription

	@Action("loadSubscription") loadSubscription!: () => Promise<void>

	public loading: boolean = false
	public errors: Array<string> = []

	/**
	 * On mount, get subscription details
	 */
	private mounted () {
		this.validateSubscription()
	}

	/**
	 * Returns formatted subscription owner
	 */
	public get registeredTo (): string {
		if (!this.subscription || !this.subscription.customerDto) return ""

		return `${this.subscription.customerDto.firstName} ` +
				`${this.subscription.customerDto.lastName} - ` +
				`${this.subscription.customerDto.email}`
	}

	/**
	 * Checks if subscription has expired
	 */
	public get isSubscriptionExpired (): boolean {
		return this.subscription
			? moment(this.subscription.expirationDate).isBefore(moment())
			: false
	}

	/**
	 * Returns formatted expiration date
	 */
	public get subscriptionExpiryDate (): string {
		return this.subscription
			? moment(this.subscription.expirationDate).format("LL LT")
			: ""
	}

	/**
	 * Returns last updated date
	 */
	public get lastUpdatedDate (): string {
		return this.subscription && this.subscription.updatedAt
			? moment(this.subscription.updatedAt!).format("LL LT")
			: ""
	}

	/**
	 * Checks if the billing button will be displayed for the current user
	 */
	public get canViewBillingPortal (): boolean {
		return this.hasAdminPermissions && this.hasBillingPortalDetails
	}

	/**
	 * Checks if the user has admin permissions
	 */
	private get hasAdminPermissions (): boolean {
		return this.currentUser.isAdmin === true
	}

	/**
	 * Checks for a link to the billing portal
	 */
	private get hasBillingPortalDetails (): boolean {
		return (
			this.subscription &&
			this.subscription.billingPortalLink &&
			this.subscription.billingPortalLink.url !== null
		) === true
	}

	/**
	 * Returns table fields
	 */
	public get tableFields (): BvTableField[] {
		const fields = [
			{ key: "component", label: "Component" },
			{ key: "definition", label: "Definition" },
			{ key: "usage", label: "Usage" }
		]

		return fields
	}

	/**
	 * Returns the billing components to display in the table
	 */
	public get tableItems (): any {
		return !this.subscription
			? []
			: [{
				component: "Cases users",
				definition: "The total number of users that can access Cases",
				casesUsers: this.subscription.casesUsers,
				casesUserLimit: this.subscription.casesUserLimit
			}]
	}

	/**
	 * Returns the subscription state badge variant
	 */
	public get subscriptionStateBadgeVariant (): string {
		if (!this.subscription || !this.subscription.state) { return "" }

		if (this.subscription.state.toLowerCase() === "trialing") {
			return "warning"
		} else if (this.subscription.isActiveState || this.subscription.state.toLowerCase() === "active") {
			return "success"
		}

		return "danger"
	}

	/**
	 * Monitors subscription changes
	 */
	@Watch("subscription")
	private onSubscriptionChanged () {
		this.validateSubscription()
	}

	/**
	 * Subscription validation
	 */
	private validateSubscription (): void {
		this.errors = []

		if (this.subscription && this.subscription.isUsingTemporaryLimit) {
			this.errors.push(`
				There has been an issue communicating with Chargify for your
				subscription information. We are currently retrying to fetch
				this data - Your User Limit has temporarily been set to
				${this.subscription.casesUserLimit}
			`)
		}
	}
}

